import { useState, useRef, useEffect } from "react";

const SearchableDropdown = ({
  label,
  options = [],
  value, // string OR array of strings
  onChange,
  placeholder = "Select...",
  disabled = false,
  isMulti = false, // 🔹 NEW: Multi-select support
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  // 🔹 Handle Display Label (Single vs Multi)
  let displayLabel = placeholder;
  const isValueEmpty = isMulti ? !value || value.length === 0 : !value;

  if (isMulti && value?.length > 0) {
    displayLabel =
      value.length === 1
        ? options.find((o) => o.value === value[0])?.label || placeholder
        : `${value.length} selected`;
  } else if (!isMulti && value) {
    displayLabel = options.find((o) => o.value === value)?.label || placeholder;
  }

  const handleClick = () => {
    if (!disabled) setOpen(!open);
  };

  const handleSelect = (optValue) => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : [];
      if (currentValues.includes(optValue)) {
        // Remove item
        onChange(currentValues.filter((v) => v !== optValue));
      } else {
        // Add item
        onChange([...currentValues, optValue]);
      }
    } else {
      onChange(optValue);
      setOpen(false);
      setSearch("");
    }
  };

  return (
    <div className="relative w-full min-w-[250px] max-w-[600px]" ref={dropdownRef}>
      <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 block mb-1">
        {label}
      </label>

      <div
        className={`border rounded px-2 py-1.5 text-sm flex justify-between items-center ${
          disabled
            ? "bg-gray-100 cursor-not-allowed border-gray-200"
            : "bg-white cursor-pointer"
        } ${open ? "ring-2 ring-blue-500 border-transparent" : "border-gray-300"}`}
        onClick={handleClick}
      >
        <span className={`truncate flex-1 ${disabled ? "text-gray-400" : "text-gray-700"}`}>
          {displayLabel}
        </span>
        <span className={`ml-2 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}>
          ▼
        </span>
      </div>

      {open && !disabled && (
        <div className="absolute z-[9999] mt-1 w-full min-w-[200px] bg-white border rounded-md shadow-2xl animate-in fade-in zoom-in duration-150">
          <div className="p-2 border-b bg-gray-50">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${label}...`}
              className="w-full border border-gray-300 px-2 py-1 text-sm rounded focus:outline-none focus:border-blue-500"
            />
          </div>

          <div className="max-h-60 overflow-y-auto overscroll-contain">
            {/* Clear Selection */}
            {!isValueEmpty && (
              <div
                className="px-3 py-2 text-sm hover:bg-red-50 cursor-pointer transition-colors text-red-600 border-b"
                onClick={() => {
                  onChange(isMulti ? [] : "");
                  if (!isMulti) setOpen(false);
                  setSearch("");
                }}
              >
                ✕ Clear Selection
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = isMulti
                  ? value?.includes(opt.value)
                  : value === opt.value;

                return (
                  <div
                    key={opt.value}
                    className={`px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer transition-colors flex items-center gap-2 ${
                      isSelected ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-600"
                    }`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    {/* 🔹 Show Checkboxes if isMulti is true */}
                    {isMulti && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="pointer-events-none rounded border-gray-300"
                      />
                    )}
                    <span className="truncate">{opt.label}</span>
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-6 text-xs text-gray-400 text-center">
                No matches found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;